import { LoadingPanel } from "@/components/LoadingPanel";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { formatDateGB } from "@/lib/format";
import { type TenancyStatus, getSupabase } from "@/lib/supabase";
import { Mail, Phone } from "lucide-react";
import { useEffect, useState } from "react";

type TenantTenancy = {
  id: string;
  property_id: string;
  property_name: string;
  status: TenancyStatus;
  start_date: string;
  end_date: string | null;
  monthly_rent_pence: number;
  hmf_tenant_id: number | null;
};

type TenantRow = {
  user_id: string;
  full_name: string | null;
  phone: string | null;
  contact_email: string | null;
  auth_email: string | null;
  tenancies: TenantTenancy[];
};

const STATUS_PILL: Record<TenancyStatus, string> = {
  upcoming: "bg-blue-500/15 text-blue-200 border-blue-500/30",
  active: "bg-emerald-500/15 text-emerald-200 border-emerald-500/30",
  ended: "bg-white/5 text-ink-dim border-line",
};

export function AdminTenants() {
  const [tenants, setTenants] = useState<TenantRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const sb = getSupabase();
      const { data, error: err } = await sb.rpc("admin_list_tenants");
      if (cancelled) return;
      if (err) setError(err.message);
      setTenants((data as unknown as TenantRow[] | null) ?? []);
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (loading) return <LoadingPanel />;

  return (
    <div className="flex flex-col gap-6">
      {error ? (
        <p role="alert" className="text-sm text-red-300">
          {error}
        </p>
      ) : null}

      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold tracking-tight">Tenants</h2>
        <span className="text-xs text-ink-dim">
          {tenants.length} tenant{tenants.length === 1 ? "" : "s"}
        </span>
      </div>

      {tenants.length === 0 ? (
        <p className="text-sm text-ink-dim">
          No tenants on file yet. Once a tenancy exists the tenant shows up here.
        </p>
      ) : (
        <ul className="grid gap-3 sm:grid-cols-2">
          {tenants.map((t) => (
            <li key={t.user_id}>
              <TenantCard tenant={t} />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function TenantCard({ tenant }: { tenant: TenantRow }) {
  const displayName =
    tenant.full_name ||
    tenant.contact_email ||
    tenant.auth_email ||
    `User ${tenant.user_id.slice(0, 8)}`;
  const email = tenant.contact_email || tenant.auth_email;
  const active = tenant.tenancies.find((t) => t.status === "active") ?? tenant.tenancies[0];

  return (
    <Card>
      <CardHeader>
        <CardTitle className="truncate">{displayName}</CardTitle>
        <CardDescription className="flex flex-col gap-1">
          {email ? (
            <a href={`mailto:${email}`} className="inline-flex items-center gap-1 text-accent">
              <Mail size={12} /> {email}
            </a>
          ) : null}
          {tenant.phone ? (
            <a href={`tel:${tenant.phone}`} className="inline-flex items-center gap-1 text-accent">
              <Phone size={12} /> {tenant.phone}
            </a>
          ) : null}
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-2">
        {active ? (
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-medium truncate">{active.property_name}</span>
            <span
              className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] uppercase tracking-wider border ${
                STATUS_PILL[active.status]
              }`}
            >
              {active.status}
            </span>
            <span className="text-xs text-ink-dim">
              £{(active.monthly_rent_pence / 100).toFixed(0)}/m
            </span>
            {active.hmf_tenant_id ? (
              <span className="text-[11px] text-ink-dim">HMF #{active.hmf_tenant_id}</span>
            ) : null}
          </div>
        ) : null}
        {tenant.tenancies.length > 0 ? (
          <p className="text-xs text-ink-dim">
            {tenant.tenancies.length === 1
              ? `Started ${formatDateGB(tenant.tenancies[0]?.start_date)}`
              : `${tenant.tenancies.length} tenancies on file (oldest ${formatDateGB(
                  tenant.tenancies[tenant.tenancies.length - 1]?.start_date,
                )})`}
          </p>
        ) : null}
      </CardContent>
    </Card>
  );
}
